import React, { FC, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Api from '~/Api';
import BackButton from '~/components/atoms/buttons/BackButton';
import Button from '~/components/atoms/buttons/Button';
import Loader from '~/components/atoms/Loader';
import HookFormTextField from '~/components/hookFormControls/TextFieldControl';
import { useSnackBarContext } from '~/components/layoutComponents/snackbar/SnackbarContext';
import Tabs from '~/components/molecules/Tabs';
import TopHeadlineRow from '~/components/molecules/TopHeadlineRow';
import { READABLE_DATE_TIME_FORMAT } from '~/constants/date';
import { DaysListRoute } from '~/modules/plans';
import { PlanDayType } from '~/modules/plans/types';
import DateService from '~/services/Date';

import styles from './styles.module.scss';

type FormDataType = {
  title: string;
  week: string;
  position: number;
};

const formSchema = yup
  .object()
  .shape({
    title: yup.string().required('Required'),
    week: yup.string().required('Required'),
    position: yup.number().required('Required'),
  })
  .required();

const Item: FC = () => {
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [day, setDay] = useState<PlanDayType>({
    id: '',
    title: '',
    week: '',
    position: 0,
    affirmations_count: 0,
    created_at: '2021-01-01',
    updated_at: '2021-01-01',
  });
  const { showSnackbar } = useSnackBarContext();
  const history = useHistory();

  const { planId, id } = useParams<{ planId: string; id: string }>();

  const form = useForm<FormDataType>({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      week: '',
      position: 0,
    },
  });

  const { handleSubmit, setValue } = form;

  const loadTheme = async (id: string): Promise<void> => {
    try {
      setIsLoading(true);

      const day = await Api.getPlanDay(id);
      setDay(day);

      setIsLoading(false);
    } catch (e: any) {
      showSnackbar(e.message);
      setIsLoading(false);
      history.push('/');
    }
  };

  const handleSuccessSubmit = async (data: FormDataType): Promise<void> => {
    try {
      setIsLoading(true);

      const day = await Api.updatePlanDay(id, data);
      showSnackbar('Updated', 'success');

      setDay(day);
      setIsLoading(false);
    } catch (e: any) {
      showSnackbar(e.message);
      setIsLoading(false);
      history.push('/');
    }
  };

  useEffect(() => {
    if (!id) {
      history.push('/');
      return;
    }
    loadTheme(id).finally(() => setIsFirstLoading(false));
  }, [id]);

  useEffect(() => {
    setValue('title', day.title);
    setValue('week', day.week);
    setValue('position', day.position);
  }, [day]);

  return (
    <div>
      <BackButton
        className={styles.back_button}
        text="Back to List"
        onClick={(): void => {
          history.push(`/plans/${planId}/days`);
        }}
      />
      <TopHeadlineRow className={styles.top_bar} text="Day card" />
      <div className={styles.container}>
        <Loader isLoading={isFirstLoading}>
          <Tabs
            className={styles.container__tabs}
            items={[
              {
                label: 'General info',
                isActive: true,
              },
            ]}
            onClickItem={(): void => {}}
          />
          <div className={styles.form_container}>
            <FormProvider {...form}>
              <form>
                <div className={styles.form_container__field}>
                  <div style={{ marginBottom: '8px' }}>
                    <HookFormTextField
                      required
                      name="title"
                      label="Title"
                      fullWidth
                    />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <HookFormTextField
                      required
                      name="week"
                      label="Week"
                      fullWidth
                    />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <HookFormTextField
                      required
                      name="position"
                      label="Position"
                      fullWidth
                      type="number"
                    />
                  </div>
                </div>
                <div className={styles.extra_info}>
                  <div className={styles.extra_info__title}>
                    <div>Created at:</div>
                    <div>Updated at:</div>
                  </div>
                  <div className={styles.extra_info__data}>
                    <div>
                      {DateService.format(
                        new Date(day.created_at),
                        READABLE_DATE_TIME_FORMAT,
                      )}
                    </div>
                    <div>
                      {DateService.format(
                        new Date(day.updated_at),
                        READABLE_DATE_TIME_FORMAT,
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(handleSuccessSubmit)}
                >
                  Update info
                </Button>
              </form>
            </FormProvider>
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default Item;
